/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Wed Nov 08 2023
 **/

import {
	FloatingArrow,
	Placement,
	arrow,
	offset,
	shift,
	useFloating,
	useHover,
	useDismiss,
	useInteractions,
	FloatingPortal
} from "@floating-ui/react";
import { useEffect, useRef, useState } from "react";

import { colors } from "@/frameworks/core/styles";

import { Maybe } from "@/frameworks/libraries/components";
import { Typography, Icon, Row } from "@/frameworks/designStuff/components";
import * as S from "./private/emotion";

const GAP = 10;
const ARROW_HEIGHT = 6;
const ARROW_WIDTH = 7;

export default function Tooltip({
	children,
	message,
	position = "top",
	hasIcon,
	isShow = false,
	onOpenChange,
	onlyTooltip = false,
	zIndex,
	...rest
}: DesignStuff.Tooltip.Props) {
	const arrowRef = useRef(null);
	const [isOpen, setIsOpen] = useState(isShow);
	useEffect(() => {
		setIsOpen(isShow);
	}, [isShow]);
	const { refs, floatingStyles, context } = useFloating({
		strategy: "fixed",
		open: isOpen,
		onOpenChange: (open) => {
			setIsOpen(open);
			onOpenChange?.(open);
		},
		placement: position as Placement,
		middleware: [
			offset(ARROW_HEIGHT + GAP),
			arrow({
				element: arrowRef
			}),
			shift({
				mainAxis: true
			})
		]
	});
	const hover = useHover(context);
	const dismiss = useDismiss(context, {
		referencePress: true
	});
	const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss]);
	return (
		<>
			{onlyTooltip ? (
				<span ref={refs.setReference} {...getReferenceProps()}>
					<S.Content ref={refs.setFloating} {...getFloatingProps()} {...rest} zIndex={zIndex}>
						<Row gap={4} verticalAlign="center">
							{hasIcon ? (
								<Row verticalAlign="top" horizonAlign="center">
									<Icon name="Info24" size={24} />
								</Row>
							) : null}
							<Typography typoSize="Body3_MD">
								<S.Message>{message}</S.Message>
							</Typography>
						</Row>
						<FloatingArrow
							width={ARROW_WIDTH}
							height={ARROW_HEIGHT}
							tipRadius={2}
							ref={arrowRef}
							context={context}
							fill={colors.neutral_00_100}
						/>
					</S.Content>
				</span>
			) : (
				<span ref={refs.setReference} {...getReferenceProps()} style={{ maxWidth: "100%", minWidth: 0 }}>
					{children}
					{isOpen && (
						<FloatingPortal>
							<S.Content
								ref={refs.setFloating}
								style={{ ...floatingStyles, ...rest.style }}
								{...getFloatingProps()}
								zIndex={zIndex}
							>
								<Row gap={4} verticalAlign="center">
									{hasIcon ? (
										<Row verticalAlign="top" horizonAlign="center">
											<Icon name="Info24" size={24} />
										</Row>
									) : null}
									<Maybe test={typeof message === "string"} fallback={message}>
										<Typography typoSize="Body3_MD">
											<S.Message>{message}</S.Message>
										</Typography>
									</Maybe>
								</Row>
								<FloatingArrow
									width={ARROW_WIDTH}
									height={ARROW_HEIGHT}
									tipRadius={2}
									ref={arrowRef}
									context={context}
									fill={colors.neutral_00_100}
									staticOffset={
										position === "left-end" ||
										position === "left-start" ||
										position === "right-end" ||
										position === "right-start"
											? "20%"
											: undefined
									}
								/>
							</S.Content>
						</FloatingPortal>
					)}
				</span>
			)}
		</>
	);
}
